import { useEffect } from "react";

type PrintEntry = {
  title: string;
  role: string;
  summary: string;
};

const EXPERIENCE: PrintEntry[] = [
  {
    title: "Confidential E-commerce Platform",
    role: "Forward Deployed Engineer",
    summary:
      "Built and maintain a custom e-commerce platform (not Shopify) with full ownership of storefront, backend services, ERP workflows, and production operations.",
  },
  {
    title: "Veza Digital (USA)",
    role: "Product Engineer",
    summary:
      "Delivered multiple production websites with strong design-to-code execution and handled integration-heavy backend work for Webflow-connected projects.",
  },
  {
    title: "BMG Studio (Germany)",
    role: "Product Engineer",
    summary:
      "Built polished client websites across front-end implementation and backend-connected functionality from concept to launch.",
  },
  {
    title: "Ad Conversion",
    role: "Product + Engineering Consultant",
    summary:
      "Implemented key auth and product modules, including Google login flows, reusable UI components, and API-integrated features.",
  },
  {
    title: "Authlink",
    role: "UI/UX Designer",
    summary:
      "Designed product-facing web and mobile interfaces, internal component systems, and dashboard surfaces for a blockchain platform.",
  },
];

const PRODUCTS: PrintEntry[] = [
  {
    title: "Shippeek",
    role: "Founder + Builder",
    summary:
      "Built a unified shipping product for multi-carrier operations with full ownership across product design, engineering, and iteration in production.",
  },
  {
    title: "Dimension",
    role: "Product Build",
    summary:
      "Built a Shopify plugin for bulk importing and editing product dimensions with workflows focused on speed and data accuracy.",
  },
  {
    title: "BrollHQ",
    role: "Product Build",
    summary:
      "Developed an AI-assisted short-form content tool for B-roll workflows, validated product direction, and sunset after initial learning phase.",
  },
];

export function CvPrintPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Yash-Bindal-CV";
    document.body.classList.add("cv-print-doc");

    let printTimeout = 0;
    let cancelled = false;

    const openPrintWhenReady = async () => {
      if ("fonts" in document) {
        try {
          await document.fonts.ready;
        } catch {
          // Ignore and continue; print still works with fallback fonts.
        }
      }

      await new Promise<void>(resolve => {
        window.requestAnimationFrame(() => resolve());
      });

      if (cancelled) {
        return;
      }

      printTimeout = window.setTimeout(() => {
        window.print();
      }, 120);
    };

    void openPrintWhenReady();

    return () => {
      cancelled = true;
      window.clearTimeout(printTimeout);
      document.title = previousTitle;
      document.body.classList.remove("cv-print-doc");
    };
  }, []);

  return (
    <main className="cvp-root flex min-h-screen w-full justify-center px-4 py-10 sm:px-6 sm:py-12">
      <article className="cvp-paper w-full max-w-[860px] rounded-lg bg-white px-4 py-6 sm:px-10 sm:py-10">
        <header className="mb-6 flex flex-col gap-3 sm:mb-7 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div>
            <h1
              className="text-[28px] leading-tight font-semibold"
              style={{ color: "rgb(var(--text-primary))", fontFamily: "var(--font-serif)" }}
            >
              Yash Bindal
            </h1>
            <p className="mt-1 text-[13px]" style={{ color: "rgb(var(--text-secondary))" }}>
              Forward Deployed Engineer
            </p>
            <p className="mt-2 break-words text-[12px]" style={{ color: "rgb(var(--text-secondary))" }}>
              itisyb@gmail.com · @yashbindal__ · India
            </p>
          </div>

          <button
            type="button"
            onClick={() => window.print()}
            className="cvp-screen-only inline-flex self-start items-center rounded-md px-2.5 py-1.5 text-[12px] font-medium sm:self-auto"
            style={{ color: "rgb(var(--text-secondary))" }}
          >
            Print PDF
          </button>
        </header>

        <section className="cvp-section mb-6 last:mb-0">
          <h2 className="mb-3 text-[12px] tracking-[0.14em] uppercase" style={{ color: "rgb(var(--text-tertiary))" }}>
            Profile
          </h2>
          <p className="text-[12px] leading-[1.75]" style={{ color: "rgb(var(--text-secondary))" }}>
            Forward Deployed Engineer with hands-on ownership from product thinking to production systems. Focused on polished
            UX, reliable front-end architecture, and shipping practical business outcomes.
          </p>
        </section>

        <section className="cvp-section mb-6 last:mb-0">
          <h2 className="mb-3 text-[12px] tracking-[0.14em] uppercase" style={{ color: "rgb(var(--text-tertiary))" }}>
            Skills
          </h2>
          <p className="text-[12px] leading-[1.75]" style={{ color: "rgb(var(--text-secondary))" }}>
            Product Design · UX · React · TypeScript · JavaScript · Front-End Architecture · Node/Bun · PostgreSQL ·
            API Design · Integrations
          </p>
        </section>

        <section className="cvp-section mb-6 last:mb-0">
          <h2 className="mb-3 text-[12px] tracking-[0.14em] uppercase" style={{ color: "rgb(var(--text-tertiary))" }}>
            Experience
          </h2>

          <div className="space-y-4">
            {EXPERIENCE.map(item => (
              <article
                key={item.title}
                className="cvp-item border-t pt-3 first:border-none first:pt-0"
                style={{ borderColor: "rgb(var(--border-color) / 0.45)" }}
              >
                <div className="mb-1.5">
                  <div>
                    <h3
                      className="text-[13px] font-medium"
                      style={{ color: "rgb(var(--text-primary))", fontFamily: "var(--font-serif)" }}
                    >
                      {item.title}
                    </h3>
                    <p className="mt-0.5 text-[12px]" style={{ color: "rgb(var(--text-secondary))" }}>
                      {item.role}
                    </p>
                  </div>
                </div>
                <p className="text-[12px] leading-[1.72]" style={{ color: "rgb(var(--text-secondary))" }}>
                  {item.summary}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="cvp-section mb-6 last:mb-0">
          <h2 className="mb-3 text-[12px] tracking-[0.14em] uppercase" style={{ color: "rgb(var(--text-tertiary))" }}>
            Products
          </h2>

          <div className="space-y-4">
            {PRODUCTS.map(item => (
              <article
                key={item.title}
                className="cvp-item border-t pt-3 first:border-none first:pt-0"
                style={{ borderColor: "rgb(var(--border-color) / 0.45)" }}
              >
                <div className="mb-1.5">
                  <div>
                    <h3
                      className="text-[13px] font-medium"
                      style={{ color: "rgb(var(--text-primary))", fontFamily: "var(--font-serif)" }}
                    >
                      {item.title}
                    </h3>
                    <p className="mt-0.5 text-[12px]" style={{ color: "rgb(var(--text-secondary))" }}>
                      {item.role}
                    </p>
                  </div>
                </div>
                <p className="text-[12px] leading-[1.72]" style={{ color: "rgb(var(--text-secondary))" }}>
                  {item.summary}
                </p>
              </article>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}
